// import { UPLOAD_IMAGES } from "../constants/visionAgentConstants";
import axios from "axios";
import * as Sentry from "@sentry/react";
import baseUrl from "../constants/baseUrl";
import { loadImagesFromIDB } from "../../jotai-store/uploadImagesPersistance";
import { set as idbSet } from "idb-keyval";
import { v4 as uuidv4 } from "uuid";
import { compressImagesParallel } from "../../Utilities/compressImages";

// Used in: src/components/Booking/ImageGalleryModal.jsx
// Upload images to external NodeJS API
export const uploadImagesToNodeApi = async (formData, signal) => {
  try {
    const nodeApiBaseUrl = process.env.REACT_APP_VISION_AGENT_NODE_API_URL;
    const uploadImagesUrl = nodeApiBaseUrl
      ? `${nodeApiBaseUrl.replace(/\/$/, "")}/upload-images`
      : null;

    if (!uploadImagesUrl) {
      throw new Error("Node API URL is not configured");
    }

    const response = await axios.post(
      // "https://nodejs-agent-apis-e2hfgea9gqd6d8em.canadacentral-01.azurewebsites.net/upload-images",
      // "http://localhost:4000/upload-images",
      uploadImagesUrl,
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
        signal,
      },
    );

    return response.data;
  } catch (error) {
    if (axios.isCancel && axios.isCancel(error)) {
      throw new Error("Upload cancelled");
    }
    Sentry.captureException(error);
    if (error.response && error.response.data && error.response.data.message) {
      throw new Error(error.response.data.message);
    }
    throw error;
  }
};

// Used in: src/pages/BookingSummary.js, src/pages/BookingPage.js (for fetching vision response by ID)
export const visionResponse = async (id, setResponse, setError) => {
  try {
    const { data } = await axios.get(`${baseUrl}Visionresponse/${id}`);
    if (data) setResponse(data);
  } catch (error) {
    console.log(error);
    Sentry.captureException(error);
    setError(id, error);
  }
};

// Used in: src/pages/BookingPage.js (for fetching popular and front items)
export const getMovingItems = async (setPopularItems, setFrontItems) => {
  try {
    const { data } = await axios.get(
      `${baseUrl}Visionresponse/GetPopularAndFrontItems`,
    );

    // Popular Items
    if (data?.data?.popularItems && Array.isArray(data.data.popularItems)) {
      const updatedPopularItems = data.data.popularItems.map((item) => ({
        ...item,
        itemId: uuidv4(),
      }));

      setPopularItems(updatedPopularItems);
    }

    // Front Items
    if (data?.data?.frontItems && Array.isArray(data.data.frontItems)) {
      const updatedFrontItems = data.data.frontItems.map((item) => ({
        ...item,
        itemId: uuidv4(),
      }));

      // Agar aapko sirf pehla object chahiye
      setFrontItems(updatedFrontItems[0]);
    }
  } catch (error) {
    console.log(error);
    Sentry.captureException(error);
  }
};

// Used in: src/pages/BookingSummary.js (for fetching vision booking items by visionId)
export const GetVisionBookingItems = async (visionId) => {
  try {
    const { data } = await axios.get(
      `${baseUrl}Visionresponse/GetVisionBookingItems/${visionId}`,
    );
    return data;
  } catch (error) {
    console.log(error);
    Sentry.captureException(error);
  }
};

export const GetBookingPriceEstimate = async (
  labourNeeded = null,
  recommendedVehicle = null,
  delay = false,
) => {
  if (delay) {
    await new Promise((resolve) => setTimeout(resolve, 1500));
  }

  const vbo = JSON.parse(localStorage.getItem("VBO")) || {};

  if (!vbo) {
    console.log("No VBO found in localStorage for price estimate.");
    return;
  }

  // Pickup Total Floors calculation
  const pickupNoOfFloors = vbo?.pickupNoOfFloors ?? 0;
  const pickupNoOfStairs = vbo?.pickupNoOfStairs ?? 0;
  const pickupMedium = vbo?.pickupMedium ?? "mainfloor";

  let pickupTotalFloors = 0;
  if (pickupMedium === "elevator") {
    pickupTotalFloors = pickupNoOfFloors;
  } else if (pickupMedium === "stairs") {
    pickupTotalFloors = pickupNoOfStairs;
  }

  // Dropoff Total Floors calculation
  const dropoffNoOfFloors = vbo?.dropoffNoOfFloors ?? 0;
  const dropoffNoOfStairs = vbo?.dropoffNoOfStairs ?? 0;
  const dropoffMedium = vbo?.dropoffMedium ?? "mainfloor";

  let dropoffTotalFloors = 0;
  if (dropoffMedium === "elevator") {
    dropoffTotalFloors = dropoffNoOfFloors;
  } else if (dropoffMedium === "stairs") {
    dropoffTotalFloors = dropoffNoOfStairs;
  }

  // Resolve the vehicle id. `recommendedVehicle` may be passed as a vehicle
  // object ({ id, ... }) or as a plain id; otherwise fall back to the VBO.
  const resolvedVehicleId =
    recommendedVehicle !== null
      ? typeof recommendedVehicle === "object"
        ? recommendedVehicle?.id
        : recommendedVehicle
      : (vbo.vehicleTypeId ?? vbo.vehicleId);

  const isTruckRequired = vbo.isTruckRequired ?? true;

  try {
    const payload = {
      items: (vbo.items ?? []).map((item) => ({
        name: item.name,
        quantity: item.quantity,
        category: item.category,
        size: item.size,
        volume: item.volume,
        weight: item.weight,
        location: item.location,
        imageID: item.imageID,
        imageURL: item.imageURL,
        isDisassemble: item.isDisassemble,
        isDisassembleRequired: item.isDisassembleRequired,
        note: item.note,
        referenceId: item.referenceId,
      })),
      pickupMedium,
      dropoffMedium,
      pickupTotalFloors,
      dropoffTotalFloors,
      durationInMins: vbo.durationInMins ?? 0,
      labourNeeded: labourNeeded !== null ? labourNeeded : vbo.labourNeeded,
      isTruckRequired,
      vehicleId: isTruckRequired ? Number(resolvedVehicleId) || 0 : 0,
      distanceInKm: vbo.distanceInKm ?? 0,
      stops: (vbo.stops ?? []).map((stop) => ({
        stop: stop.stop,
        personName: stop.personName,
        personPhone: stop.personPhone,
        instructions: stop.instructions,
        bookingId: stop.bookingId,
        lat: stop.lat,
        long: stop.long,
        stopMedium: stop.stopMedium,
        stopTotalFloors: stop.stopTotalFloors,
      })),
    };

    const { data } = await axios.post(
      `${baseUrl}VisionBookingAPI/GetBookingPriceEstimate`,
      payload,
    );

    return data.data;
  } catch (error) {
    console.log(error);
    Sentry.captureException(error);
  }
};

// Used in: src/pages/BookingSummary.js (for fetching vision booking by visionId)
export const getVisionBookingByVisionId = async (visionId) => {
  try {
    const res = await axios.get(
      `${baseUrl}VisionBookingAPI/GetVisionBooking?visionId=${visionId}`,
    );
    return res.data;
  } catch (error) {
    console.log(error);
    Sentry.captureException(error);
  }
};

// Used in: src/pages/BookingPage.js (for creating a vision booking)
export const createVisionBooking = async (payload) => {
  const response = await axios.post(
    `${baseUrl}VisionBookingAPI/CreateVisionBooking`,
    payload,
  );
  return response.data;
};

// Used in: src/pages/BookingPage.js (for updating a vision booking)
export const updateVisionBooking = async (payload) => {
  const response = await axios.post(
    `${baseUrl}VisionBookingAPI/UpdateVisionBooking`,
    payload,
  );
  return response.data;
};

export const saveVisionDataToBackend = async (setItems = () => { }) => {
  try {
    const items = JSON.parse(localStorage.getItem("VBItems")) || [];
    const summaryData = JSON.parse(localStorage.getItem("VBSummary")) || {};
    let uploadedImages = await loadImagesFromIDB();

    // Agar IDB empty hai, placeholder image add hogi (kyunki backend ko kam se kam ek image chahiye hoti hai VisionId Create Karne k liye)
    if (!uploadedImages || uploadedImages.length === 0) {
      // if user didn't upload any image, we will send a placeholder image to ensure the vision booking process
      //  continues smoothly. This is because the backend requires at least one image to create a VisionId,
      //if user want to create booking with popular/front items without uploading image, this will ensure that they can do it without any issue. Once the booking is created, user can edit the booking and upload images for better accuracy.

      const placeholderFile = await fetch("/noImageUpload.png") // <-- root-relative path
        .then((res) => res.blob())
        .then(
          (blob) => new File([blob], "noImageUpload.png", { type: blob.type }),
        );

      uploadedImages = [
        {
          file: placeholderFile,
          id: "noImageUpload.png",
          name: "noImageUpload.png",
        },
      ];
    }


    // --- Compress large images in parallel ---
    uploadedImages = await compressImagesParallel(uploadedImages);

    // --- Prepare FormData ---
    const formData = new FormData();
    uploadedImages.forEach((img, index) => {
      if (img.file) {
        formData.append(`request[${index}].File`, img.file, img.name);
        formData.append(`request[${index}].ImageRefId`, img.id);
      }
    });


    items.forEach((item, index) => {
      Object.keys(item).forEach((key) => {
        formData.append(`analyzeitems.Items[${index}].${key}`, item[key]);
      });
    });

    formData.append("analyzeitems.Summary.TotalItems", items.length);
    formData.append(
      "analyzeitems.Summary.ClutterLevel",
      summaryData.clutterLevel || "Normal",
    );
    formData.append("analyzeitems.Summary.Notes", summaryData.notes || "");

    // --- API 1: Save Image Request ---
    const firstResponse = await axios.post(
      `${baseUrl}Visionresponse/VisionSaveImageRequest`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } },
    );

    if (!firstResponse.data.isSuccess || !firstResponse.data.data) {
      throw new Error("Failed to save images.");
    }

    const { visionId } = firstResponse.data.data;

    // --- API 2: Vision Items Analysis ---
    // Note: Isme hum items ke names bhej rahe hain
    await axios.post(
      `${baseUrl}Visionresponse/VisionItemsAnalysis?visionId=${visionId}`,
      items.map((item) => item.name),
      { headers: { "Content-Type": "application/json-patch+json" } },
    );

    // --- API 3: Get Final Booking Items ---
    const getVisionBookingItems = await GetVisionBookingItems(visionId);

    if (getVisionBookingItems.isSuccess) {
      const finalData = getVisionBookingItems.data;
      const newItems = finalData.items || [];
      const newSummary = finalData.summary || {};

      // --- Sync Everything ---
      const currentVBO = JSON.parse(localStorage.getItem("VBO")) || {};
      localStorage.setItem(
        "VBO",
        JSON.stringify({ ...currentVBO, items: newItems }),
      );
      localStorage.setItem("VBItems", JSON.stringify(newItems));
      localStorage.setItem("visionReqId", visionId.toString());
      localStorage.setItem("VBSummary", JSON.stringify(newSummary));

      // IDB clear karein sirf success par
      await idbSet("uploaded_images_persistance", []);

      setItems(newItems);

      return {
        isSuccess: true,
        newItems,
        visionId,
        newSummary,
      };
    }

    return { isSuccess: false, message: "Could not fetch finalized items." };
  } catch (error) {
    console.error("API Chain Error:", error.response?.data || error.message);
    return {
      isSuccess: false,
      message:
        error.response?.data?.message ||
        error.message ||
        "Something went wrong",
    };
  }
};
